import { saveAs } from "file-saver";
import JSZip from "jszip";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button, Typography, Box, Chip, Stack, Divider } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DataTable, {
  ColumnDef,
  SortOrder,
} from "../../components/DataTable/DataTable";
import { brand, gray } from "../../theme/themePrimitives";
import { Filter, FilterGroup } from "./components/Filter";
import { AddNewFileModal } from "./components/AddNewFileModal";
import dayjs, { Dayjs } from "dayjs";
import { usePageMetadata } from "../../context/PageMetadataContext";
import { useToast } from "../../context/ToastContext";
import { usePortal } from "../../context/PortalsContext";
import {
  FileHubDeleteResponseStatus,
  FileHubFilesQueryParams,
  GetFileHubFilesPaginatedResult,
  GetFileHubFilesResult,
  GetS3PresignedUrlsDownloadResponse,
  GetS3PresignedUrlsDownloadResult,
} from "../../services/api/types/fileHub/fileHub";
import { useParams } from "react-router-dom";
import {
  deleteFileHubFiles,
  getS3PresignedUrlsDownload,
  getAllFileHubFiles,
  subscribeForEmailNotification,
} from "../../services/api/apiClient";
import { FileHubEmailNotificationToggle } from "./components/FileHubEmailNotification";
import { CustomActionIconLink } from "../../components/CustomActionIconLink/CustomActionIconLink";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ConfirmModal } from "../../components/ConfirmModal/ConfirmModal";
import InformationModal from "./components/InformationModal";
import {
  PresignedUrlBodyParams,
  PresignFileType,
} from "../../services/api/types/portalConfiguration/uploadPhoto";
import { formatBytes } from "../../utils/helpers";

const SELECTED_FILES_KEY = "fileHubSelectedFiles";
const APPLIED_FILTERS_LIST_KEY = "fileHubAppliedFiltersList";
const APPLIED_DATE_FROM_KEY = "fileHubAppliedDateFrom";
const APPLIED_DATE_TO_KEY = "fileHubAppliedDateTo";

const triggerBrowserDownload = (downloadUrl: string) => {
  window.open(downloadUrl, "_self", "noopener,noreferrer");
};

export const FileHub: React.FC = () => {
  const [search, setSearch] = useState("");
  const [fileHubResp, setFileHubResp] =
    useState<GetFileHubFilesPaginatedResult>();
  const [loading, setLoading] = useState(false);
  const [storageLoaded, setStorageLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [showAddFileDialog, setShowAddFileDialog] = useState(false);
  const [sort, setSort] = useState<{ orderBy: string; order: SortOrder }>({
    orderBy: "fileName",
    order: "asc",
  });
  const [selectedFiles, setSelectedFiles] = useState<GetFileHubFilesResult[]>(
    [],
  );
  const [openFromDatePicker, setOpenFromDatePicker] = useState(false);
  const [openToDatePicker, setOpenToDatePicker] = useState(false);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [appliedDateFrom, setAppliedDateFrom] = useState<Dayjs | null>(null);
  const [appliedDateTo, setAppliedDateTo] = useState<Dayjs | null>(null);
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const [emailSubscriptionLoading, setEmailSubscriptionLoading] =
    useState(false);
  const [confirmDeletionModal, setConfirmDeletionModal] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [informationModalInfo, setInformationModalInfo] = useState<{
    countNotAllowed: number;
    countDeleted: number;
    notAllowedFileHubs: Array<{ name: string; user: string }>;
  }>({
    countNotAllowed: 0,
    countDeleted: 0,
    notAllowedFileHubs: [],
  });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [editFileUid, setEditFileUid] = useState<string | null>(null);

  const [filtersList, setFiltersList] = useState<FilterGroup[]>([
    {
      title: "File Type",
      items: [
        { id: "type-confidential", name: "Confidential", checked: false },
        { id: "type-regular", name: "Regular", checked: false },
      ],
    },
  ]);

  const [appliedFiltersList, setAppliedFiltersList] = useState<FilterGroup[]>([
    {
      title: "File Type",
      items: [
        { id: "type-confidential", name: "Confidential", checked: false },
        { id: "type-regular", name: "Regular", checked: false },
      ],
    },
  ]);

  const { portalUid } = useParams();
  const { showToast } = useToast();
  const { portals } = usePortal();
  const previousSelectedPortalUid = useRef<string | undefined>(
    portals.selectedPortal?.portalUid,
  );
  const previousPortalUid = useRef<string | undefined>(portalUid);
  const anySelected = selectedFiles.length > 0;

  const persistSelection = (filesToPersist: GetFileHubFilesResult[]) => {
    if (!filesToPersist.length) {
      localStorage.removeItem(SELECTED_FILES_KEY);
      localStorage.removeItem("selectedFiles");
      return;
    }

    localStorage.setItem(SELECTED_FILES_KEY, JSON.stringify(filesToPersist));
  };

  const clearAllSelectionStorage = () => {
    [SELECTED_FILES_KEY, "selectedFiles"].forEach((key) =>
      localStorage.removeItem(key),
    );
  };

  const clearAllFilterStorage = () => {
    [
      APPLIED_FILTERS_LIST_KEY,
      APPLIED_DATE_FROM_KEY,
      APPLIED_DATE_TO_KEY,
    ].forEach((key) => localStorage.removeItem(key));
  };

  const persistAppliedFilters = (
    nextAppliedFiltersList: FilterGroup[],
    nextAppliedDateFrom: Dayjs | null,
    nextAppliedDateTo: Dayjs | null,
  ) => {
    localStorage.setItem(
      APPLIED_FILTERS_LIST_KEY,
      JSON.stringify(nextAppliedFiltersList),
    );

    const persistDate = (key: string, value: Dayjs | null) => {
      if (value) {
        localStorage.setItem(key, value.toISOString());
      } else {
        localStorage.removeItem(key);
      }
    };

    persistDate(APPLIED_DATE_FROM_KEY, nextAppliedDateFrom);
    persistDate(APPLIED_DATE_TO_KEY, nextAppliedDateTo);
  };

  // Clear everything when changing portal
  useEffect(() => {
    if (
      previousPortalUid.current !== undefined &&
      previousPortalUid.current !== portalUid
    ) {
      clearAllSelectionsAndFilters();
    }

    // Update the ref after the comparison
    previousPortalUid.current = portalUid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid]);

  useEffect(() => {
    const currentPortal = portals.selectedPortal?.portalUid;
    if (
      previousSelectedPortalUid.current !== undefined &&
      previousSelectedPortalUid.current !== currentPortal
    ) {
      clearAllSelectionsAndFilters();
    }
    previousSelectedPortalUid.current = currentPortal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.selectedPortal?.portalUid]);

  useEffect(() => {
    const parseStoredFiles = (key: string): GetFileHubFilesResult[] => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    const parseStoredFilters = (key: string): FilterGroup[] | null => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    };

    const parseStoredDate = (key: string): Dayjs | null => {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      const parsed = dayjs(stored);
      return parsed.isValid() ? parsed : null;
    };

    const storedAppliedFiltersList = parseStoredFilters(
      APPLIED_FILTERS_LIST_KEY,
    );

    if (storedAppliedFiltersList) {
      setAppliedFiltersList(storedAppliedFiltersList);
      setFiltersList(storedAppliedFiltersList);
    }

    const storedAppliedDateFrom = parseStoredDate(APPLIED_DATE_FROM_KEY);
    const storedAppliedDateTo = parseStoredDate(APPLIED_DATE_TO_KEY);

    setAppliedDateFrom(storedAppliedDateFrom);
    setAppliedDateTo(storedAppliedDateTo);
    setDateFrom(storedAppliedDateFrom);
    setDateTo(storedAppliedDateTo);

    const storedPortal = sessionStorage.getItem("selectedPortal");
    const currentPortal = portalUid || portals.selectedPortal?.portalUid || "";

    if (storedPortal && currentPortal && storedPortal !== currentPortal) {
      clearAllSelectionsAndFilters();
    }

    const selectedFromStorage = parseStoredFiles(SELECTED_FILES_KEY);
    const legacySelected = parseStoredFiles("selectedFiles");

    if (legacySelected.length && !selectedFromStorage.length) {
      persistSelection(legacySelected);
      localStorage.removeItem("selectedFiles");
    }

    const normalizedSelected = selectedFromStorage.length
      ? selectedFromStorage
      : legacySelected;

    setSelectedFiles(normalizedSelected);

    setStorageLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!storageLoaded) return;
    fetchFileHubFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storageLoaded,
    search,
    rowsPerPage,
    page,
    sort.orderBy,
    sort.order,
    portalUid,
  ]);

  const fetchFileHubFiles = async (options?: {
    skipSelectionMerge?: boolean;
  }) => {
    if (!portalUid) return;
    setLoading(true);

    const URLParams: FileHubFilesQueryParams = {
      limit: rowsPerPage,
      offset: (page - 1) * rowsPerPage,
      orderBy: sort.orderBy,
      sort: sort.order,
    };

    if (search) {
      URLParams.search = search;
    }

    const params = new URLSearchParams(window.location.search);
    const urlSearch = params.get("search");
    if (urlSearch) {
      URLParams.search = urlSearch;
    }

    try {
      const response = await getAllFileHubFiles(portalUid, URLParams);
      setFileHubResp(response);

      if (!emailSubscriptionLoading) {
        setEmailSubscribed(Boolean(response.subscription));
      }

      // Merge selected files with fetched files to get latest data
      if (
        !options?.skipSelectionMerge &&
        selectedFiles.length > 0 &&
        response.rows
      ) {
        const fetchedFileMap = new Map<string, GetFileHubFilesResult>(
          response.rows.map((file: GetFileHubFilesResult) => [
            file.FileHubUid,
            file,
          ]),
        );

        const updatedSelectedFiles: GetFileHubFilesResult[] = selectedFiles.map(
          (selectedFile) => {
            const fetchedFile = fetchedFileMap.get(selectedFile.FileHubUid);
            // Use fetched file if available (has latest data), otherwise keep selected
            return fetchedFile || selectedFile;
          },
        );

        setSelectedFiles(updatedSelectedFiles);
      }
    } catch (error) {
      console.log("ERROR: ", error);
      setFileHubResp(undefined);
    } finally {
      setLoading(false);
    }
  };

  usePageMetadata({
    title: "File Hub",
    breadcrumbs: [],
  });

  const handleSortChange = (orderBy: string, order: SortOrder) => {
    setSort({ orderBy, order });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleCheckboxChange = (groupIndex: number, itemIndex: number) => {
    setFiltersList((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[groupIndex].items[itemIndex].checked =
        !copy[groupIndex].items[itemIndex].checked;
      return copy;
    });
  };

  const handleDeleteSelectedFiles = async () => {
    if (!portalUid || selectedFiles.length === 0) return;

    try {
      const fileUids = selectedFiles.map((file) => file.FileHubUid);
      const res: FileHubDeleteResponseStatus = await deleteFileHubFiles(
        portalUid,
        fileUids,
      );
      // Clear selection immediately after delete so header count resets
      persistSelection([]);
      setSelectedFiles([]);

      setInformationModalInfo({
        countNotAllowed: res.countNotAllowed ?? 0,
        countDeleted: res.countDeleted ?? 0,
        notAllowedFileHubs: res.notAllowedFileHubs ?? [],
      });
      const hasWarnings = res.status === "WARNING" || res.countNotAllowed > 0;
      setShowInformationModal(hasWarnings);
      if (!hasWarnings) {
        const deletedCount = res.countDeleted ?? selectedFiles.length;
        showToast(
          deletedCount === 1
            ? "File removed successfully"
            : "Files removed successfully",
          "success",
        );
      }
      fetchFileHubFiles({ skipSelectionMerge: true });
    } catch (error) {
      console.error("Error deleting files:", error);
    } finally {
      setConfirmDeletionModal(false);
    }
  };

  const clearAllSelectionsAndFilters = () => {
    const clearedFilters = filtersList.map((g) => ({
      ...g,
      items: g.items.map((i) => ({ ...i, checked: false })),
    }));
    setFiltersList(clearedFilters);
    setAppliedFiltersList(clearedFilters);
    setDateFrom(null);
    setDateTo(null);
    setAppliedDateFrom(null);
    setAppliedDateTo(null);
    setPage(1);
    clearAllFilterStorage();
    clearAllSelectionStorage();
    setSelectedFiles([]);
  };

  const saveFilters = () => {
    const nextAppliedFilters = JSON.parse(JSON.stringify(filtersList));
    setAppliedFiltersList(nextAppliedFilters);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setShowFilter(false);
    setPage(1);
    persistAppliedFilters(nextAppliedFilters, dateFrom, dateTo);
  };

  const handleRemoveFilter = (groupId: string, itemId: string) => {
    const groupIndex = appliedFiltersList.findIndex((g) => g.title === groupId);
    if (groupIndex === -1) return;

    const itemIndex = appliedFiltersList[groupIndex].items.findIndex(
      (it) => it.id === itemId,
    );
    if (itemIndex === -1) return;

    const updatedApplied = JSON.parse(JSON.stringify(appliedFiltersList));
    updatedApplied[groupIndex].items[itemIndex].checked = false;
    setAppliedFiltersList(updatedApplied);

    const updatedPending = JSON.parse(JSON.stringify(filtersList));
    updatedPending[groupIndex].items[itemIndex].checked = false;
    setFiltersList(updatedPending);
    persistAppliedFilters(updatedApplied, appliedDateFrom, appliedDateTo);
  };

  const handleRemoveDateFilter = () => {
    setAppliedDateFrom(null);
    setAppliedDateTo(null);
    setDateFrom(null);
    setDateTo(null);
    persistAppliedFilters(appliedFiltersList, null, null);
  };

  const handleDateFromChange = (next: Dayjs | null) => {
    setDateFrom(next);
  };

  const handleDateToChange = (next: Dayjs | null) => {
    setDateTo(next);
  };

  const handleSelectionChange = (updatedFiles: GetFileHubFilesResult[]) => {
    setSelectedFiles(updatedFiles);
    persistSelection(updatedFiles);
  };

  const handleEmailSubscriptionChange = async (nextValue: boolean) => {
    if (!portalUid) return;
    const previous = emailSubscribed;
    setEmailSubscribed(nextValue);
    setEmailSubscriptionLoading(true);
    try {
      await subscribeForEmailNotification(portalUid, nextValue);
      showToast(
        nextValue
          ? "Email notifications enabled"
          : "Email notifications disabled",
        nextValue ? "success" : "warning",
      );
    } catch (error: any) {
      setEmailSubscribed(previous);
      showToast(
        error?.response?.data || "Failed to update email notification.",
        "error",
      );
    } finally {
      setEmailSubscriptionLoading(false);
    }
  };

  const appliedFilters = useMemo(() => {
    return appliedFiltersList
      .flatMap((g) => g.items.map((i) => ({ group: g.title, ...i })))
      .filter((i) => i.checked);
  }, [appliedFiltersList]);

  const filteredData = useMemo(() => {
    let filtered = [...(fileHubResp?.rows ?? [])];

    // Filter by file type
    const typeFilters = appliedFiltersList
      .find((f) => f.title === "File Type")
      ?.items.filter((item) => item.checked)
      .map((item) => item.name);

    if (typeFilters && typeFilters.length > 0) {
      filtered = filtered.filter((item) => typeFilters.includes(item.type));
    }

    // Filter by date range
    if (appliedDateFrom || appliedDateTo) {
      filtered = filtered.filter((item) => {
        const itemDate = dayjs(item.dateCreated);
        if (appliedDateFrom && appliedDateTo) {
          return (
            (itemDate.isAfter(appliedDateFrom, "day") ||
              itemDate.isSame(appliedDateFrom, "day")) &&
            (itemDate.isBefore(appliedDateTo, "day") ||
              itemDate.isSame(appliedDateTo, "day"))
          );
        } else if (appliedDateFrom) {
          return (
            itemDate.isAfter(appliedDateFrom, "day") ||
            itemDate.isSame(appliedDateFrom, "day")
          );
        } else if (appliedDateTo) {
          return (
            itemDate.isBefore(appliedDateTo, "day") ||
            itemDate.isSame(appliedDateTo, "day")
          );
        }
        return true;
      });
    }

    return filtered;
  }, [fileHubResp?.rows, appliedFiltersList, appliedDateFrom, appliedDateTo]);

  const requestDownloadUrls = async (
    fileNames: string[],
  ): Promise<GetS3PresignedUrlsDownloadResponse> => {
    if (!portalUid) {
      return { files: [], notFound: [] };
    }
    const body: PresignedUrlBodyParams = {
      fileNames,
      fileName: "",
      contentType: "",
      fileType: PresignFileType.FILE_HUB,
      isDownload: true,
    };
    return getS3PresignedUrlsDownload(
      portalUid,
      body,
    ) as Promise<GetS3PresignedUrlsDownloadResponse>;
  };

  const downloadSingle = async (file: GetFileHubFilesResult) => {
    if (!portalUid) return;

    setDownloadLoading(true);
    try {
      const res = await requestDownloadUrls([file.fileName]);
      const first = res?.files?.[0];
      triggerBrowserDownload(first.downloadUrl);
      showToast("Download completed", "success");
    } catch (e: any) {
      showToast(e?.response?.data || "Failed to download file.", "error");
    } finally {
      setDownloadLoading(false);
    }
  };

  const downloadBulk = async (files: GetFileHubFilesResult[]) => {
    if (!portalUid) return;
    if (!files.length) {
      showToast("Select at least one file to download.", "warning");
      return;
    }

    setDownloadLoading(true);
    showToast("Download started", "info");
    try {
      const names = files.map((f) => f.fileName);
      const res = await requestDownloadUrls(names);
      const hasNotFound = Boolean(res?.notFound?.length);

      if (res.files.length === 1) {
        triggerBrowserDownload(res.files[0].downloadUrl);
      } else {
        const zip = new JSZip();
        await Promise.all(
          (res.files as GetS3PresignedUrlsDownloadResult[])
            .filter((item) => item?.downloadUrl)
            .map(async (item) => {
              const response = await fetch(item.downloadUrl);
              const blob = await response.blob();
              zip.file(item.fileName, blob);
            }),
        );

        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `files_${new Date().getTime()}.zip`);
      }

      if (hasNotFound) {
        showToast(
          `Downloaded ${res.files.length} file(s). Not found: ${res.notFound.join(", ")}`,
          "warning",
        );
      } else {
        showToast("Download completed", "success");
      }
    } catch (e: any) {
      showToast(e?.response?.data || "Failed to download files.", "error");
    } finally {
      setDownloadLoading(false);
    }
  };

  const actions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {anySelected && (
        <>
          <CustomActionIconLink
            title="Remove"
            icon={<DeleteOutlineIcon />}
            onClick={() => setConfirmDeletionModal(true)}
          />
          <CustomActionIconLink
            title="Download"
            icon={<FileDownloadOutlinedIcon />}
            onClick={() => downloadBulk(selectedFiles)}
          />
          <Divider orientation="vertical" flexItem />
        </>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          sx={{
            px: 2,
            borderRadius: "12px",
            backgroundColor: `${brand[600]} !important`,
            color: `white`,
            "&:hover": {
              bgcolor: `${brand[700]} !important`,
              textDecoration: "underline",
            },
          }}
          onClick={() => {
            setShowAddFileDialog(true);
          }}
        >
          Add new file
        </Button>
        <Button
          id="filehub-filter-button"
          sx={{
            borderRadius: "12px",
            padding: "16px",
            border: `1px solid ${gray[300]}`,
          }}
          startIcon={
            <FilterListIcon
              sx={{
                color: gray[700],
              }}
            />
          }
          onClick={() => {
            setShowFilter(!showFilter);
          }}
        >
          Filter
        </Button>
      </Stack>
    </Stack>
  );

  const isEditDisabled = true;

  const columns: ColumnDef<GetFileHubFilesResult>[] = [
    {
      id: "fileName",
      label: "Name",
      sortable: true,
      render: (item) => (
        <Typography
          component="span"
          sx={{
            color: brand[600],
            textDecoration: "underline",
            cursor: downloadLoading ? "progress" : "pointer",
            fontWeight: 400,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!downloadLoading) downloadSingle(item);
          }}
        >
          {item.fileName}
        </Typography>
      ),
    },
    {
      id: "CreatedAt",
      label: "Date",
      sortable: true,
      getValue: (item) => item.dateCreated,
    },
    {
      id: "user",
      label: "User",
      sortable: true,
      getValue: (item) => item.user,
    },
    {
      id: "size",
      label: "Size",
      sortable: true,
      getValue: (item) => Number(item.size) || 0,
      render: (item) => (
        <Typography component="span">
          {formatBytes(Number(item.size) || 0)}
        </Typography>
      ),
    },
    {
      id: "type",
      label: "Type",
      sortable: true,
      getValue: (item) => item.type,
    },
    {
      id: "actions",
      label: "",
      width: "100px",
      align: "center",
      render: (file) => (
        <Typography
          onClick={(e) => {
            e.stopPropagation();

            if (isEditDisabled) return;

            setShowAddFileDialog(true);
            setEditFileUid(file.FileHubUid);
          }}
          sx={{
            color: isEditDisabled ? gray[400] : brand[600],
            textTransform: "none",
            cursor: isEditDisabled ? "not-allowed" : "pointer",
            fontWeight: 600,

            "&:hover": isEditDisabled
              ? {}
              : {
                  textDecoration: `underline`,
                  color: brand[700],
                },
          }}
        >
          Edit
        </Typography>
      ),
    },
  ];

  const hasDateFilter = appliedDateFrom || appliedDateTo;
  const showFilters = appliedFilters.length > 0 || hasDateFilter;
  const showHeaderContent = showFilters || anySelected;

  return (
    <>
      <FileHubEmailNotificationToggle
        checked={emailSubscribed}
        onChange={handleEmailSubscriptionChange}
        disabled={emailSubscriptionLoading}
      />
      <DataTable
        data={filteredData ?? []}
        columns={columns}
        totalCount={fileHubResp?.count || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sort={sort}
        onSortChange={handleSortChange}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or user"
        selectedCountTitle="Selected files"
        enableSelection={true}
        selectedRows={selectedFiles}
        onSelectionChange={handleSelectionChange}
        isLoading={loading}
        actions={actions}
        headerContent={
          showHeaderContent ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: 1,
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {showFilters ? (
                  <>
                    {appliedFilters.map((f) => (
                      <Chip
                        key={f.id}
                        label={`${f.group}: ${f.name}`}
                        size="medium"
                        variant="outlined"
                        onDelete={() => handleRemoveFilter(f.group, f.id)}
                      />
                    ))}
                    {hasDateFilter && (
                      <Chip
                        label={`Date: ${
                          appliedDateFrom
                            ? appliedDateFrom.format("DD-MMM-YYYY")
                            : ""
                        }${appliedDateFrom && appliedDateTo ? " to " : ""}${
                          appliedDateTo
                            ? appliedDateTo.format("DD-MMM-YYYY")
                            : ""
                        }`}
                        size="medium"
                        variant="outlined"
                        onDelete={handleRemoveDateFilter}
                      />
                    )}
                  </>
                ) : null}
              </Box>
              <Button
                size="small"
                variant="text"
                sx={{
                  color: brand[600],
                  fontSize: "16px",
                  fontWeight: 600,
                  backgroundColor: "transparent !important",
                }}
                onClick={() => clearAllSelectionsAndFilters()}
              >
                Clear all
              </Button>
            </Box>
          ) : undefined
        }
        getRowId={(item) => item.FileHubUid}
      />

      <Filter
        filtersList={filtersList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        handleCheckboxChange={handleCheckboxChange}
        saveFilters={saveFilters}
        clearFilters={clearAllSelectionsAndFilters}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
        openFromDatePicker={openFromDatePicker}
        onOpenFromDatePickerChange={() => setOpenFromDatePicker(true)}
        onCloseFromDatePicker={() => setOpenFromDatePicker(false)}
        openToDatePicker={openToDatePicker}
        onOpenToDatePickerChange={() => setOpenToDatePicker(true)}
        onCloseToDatePicker={() => setOpenToDatePicker(false)}
      />

      <AddNewFileModal
        open={showAddFileDialog}
        isEditMode={!!editFileUid}
        fileHubId={editFileUid}
        onClose={() => {
          setShowAddFileDialog(false);
          setEditFileUid(null);
        }}
        onSuccess={() => {
          fetchFileHubFiles({ skipSelectionMerge: true });
        }}
      />
      <ConfirmModal
        openConfirmModal={confirmDeletionModal}
        setOpenConfirmModal={setConfirmDeletionModal}
        modalTitle="Remove file"
        modalSubtitle="Are you sure you want to remove this file? "
        confirmButtonClick={handleDeleteSelectedFiles}
      />
      <InformationModal
        open={showInformationModal}
        onClose={() => setShowInformationModal(false)}
        removedFiles={informationModalInfo.countDeleted}
        notRemovedFiles={informationModalInfo.countNotAllowed}
        notRemovableFiles={informationModalInfo.notAllowedFileHubs}
      />
    </>
  );
};
